import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackgroundJobs } from './jobs/BackgroundJobsContext';
import { JOB_REGISTRY, jobCompletion } from './jobs/jobRegistry';

function followHref(navigate, href) {
  if (!href) return;
  if (/^https?:\/\//i.test(href)) {
    // External signed URL (download) — open in a new tab so an inline-playable
    // file can't navigate the SPA's own tab away.
    const a = document.createElement('a');
    a.href = href;
    a.rel = 'noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    navigate(href);
  }
}

// Banner-only labels for aggregate job types that aren't in the fire-and-forget
// JOB_REGISTRY (index/extraction/transcription fan-outs). Registry types
// (redact_media, export_documents, import_checklist) fall through to their
// registry label below.
const AGG_LABELS = {
  index_documents: 'Indexing for search',
  complete_extraction: 'Upgrading to deep search',
  full_transcription: 'Transcribing media',
  reanalyze_with_full_text: 'Reanalyzing',
  transcode_media: 'Transcoding media',
  upgrade_transcription: 'Generating full transcript',
};

function jobLabel(type) {
  return AGG_LABELS[type]
    || JOB_REGISTRY[type]?.label
    || type?.replace(/_/g, ' ')
    || 'Background job';
}

// Friendly detail for known single-job phases (no doc-count progress). Lets a
// redaction render read "Rendering… → Preparing playback…" instead of "Working…".
const PHASE_LABELS = {
  resolving: 'Working…',
  downloading: 'Working…',
  rendering: 'Rendering…',
  registering: 'Finishing up…',
  preparing_playback: 'Preparing playback…',
  importing: 'Importing…',
};

function formatElapsed(seconds) {
  if (!seconds || seconds < 1) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatRemaining(seconds) {
  if (seconds == null || !isFinite(seconds) || seconds < 1) return '';
  if (seconds < 60) return `~${Math.round(seconds)}s left`;
  const m = Math.round(seconds / 60);
  return m === 1 ? '~1m left' : `~${m}m left`;
}

/**
 * Collapse all jobs of the same type into one banner row.
 *
 * Rationale: the user's mental model is "indexing is happening" — they
 * don't care that internally we may have spawned multiple BackgroundJob
 * rows or that one of them was failed by the stale-queue watchdog while
 * a sibling kept processing.  We surface one progress signal per work
 * type: how far along, how long left, and whether it's done or failed.
 */
function summarizeGroup(type, groupJobs) {
  const active = groupJobs.filter(j => j.status === 'queued' || j.status === 'processing');
  const completed = groupJobs.filter(j => j.status === 'completed');
  const failed = groupJobs.filter(j => j.status === 'failed');

  // Sum aggregate numbers from active jobs.  Failed jobs don't contribute —
  // their docs are typically re-submitted into a new active job.
  let indexed = 0;
  let total = 0;
  let maxElapsed = 0;
  let anyProcessing = false;
  let lastPhase = null;
  // A registry type may read its own progress shape (an export counts files).
  const ownProgress = JOB_REGISTRY[type]?.progress;
  let verb = null;
  for (const j of active) {
    const p = j.progress || {};
    const own = ownProgress ? ownProgress(p) : null;
    if (own) { verb = own.verb; }
    indexed += own ? own.done : (p.indexed_so_far || 0);
    total += own ? own.total : (p.total_documents || 0);
    if (p.phase) lastPhase = p.phase;
    if ((j.elapsed_seconds || 0) > maxElapsed) maxElapsed = j.elapsed_seconds || 0;
    if (j.status === 'processing') anyProcessing = true;
  }

  // ── Status priority: active > completed > failed ───
  if (active.length > 0) {
    const pct = total > 0 ? Math.min(99, Math.round((indexed / total) * 100)) : null;
    let detail;
    if (!anyProcessing) {
      detail = 'Queued — waiting to start';
    } else if (total > 0) {
      detail = verb ? `${verb} ${indexed} of ${total}` : `${indexed} of ${total} indexed`;
      // ETA = remaining / rate.  Only show once we have signal: at least 5s
      // elapsed and some progress, so the rate isn't computed off near-zero.
      if (maxElapsed > 5 && indexed > 0) {
        const rate = indexed / maxElapsed; // docs per second
        const eta = (total - indexed) / rate;
        const remaining = formatRemaining(eta);
        if (remaining) detail += ` · ${remaining}`;
      }
    } else {
      detail = PHASE_LABELS[lastPhase] || 'Working…';
    }
    return {
      key: `${type}-active`,
      kind: 'active',
      jobType: type,
      label: jobLabel(type),
      detail,
      pct,
      elapsed: maxElapsed,
    };
  }

  if (completed.length > 0) {
    const totalIndexed = completed.reduce((sum, j) => {
      const r = j.progress || {};
      return sum + (r.newly_indexed || 0);
    }, 0);

    // complete_extraction reports its own success/failure split. Surface
    // it inline — a "Done" banner that hides 12 failed docs is exactly
    // how the In re Epstein incident slipped past the user.
    let detail;
    const failedDetails = [];
    let totalFailed = 0;
    if (type === 'complete_extraction') {
      let totalCompleted = 0;
      for (const j of completed) {
        const r = j.progress || {};
        totalCompleted += r.documents_completed || 0;
        totalFailed += r.documents_failed || 0;
        if (Array.isArray(r.failed_details)) {
          for (const f of r.failed_details) failedDetails.push(f);
        }
      }
      detail = totalFailed > 0
        ? `Done — ${totalCompleted} upgraded, ${totalFailed} failed`
        : `Done — ${totalCompleted} upgraded`;
    } else if (JOB_REGISTRY[type]?.completion && type === 'export_documents') {
      const latest = completed.reduce((best, j) => ((!best || (j.created_at || '') > (best.created_at || '')) ? j : best), null);
      detail = jobCompletion(type, latest?.result || latest?.progress || {}, {})?.message || 'Ready';
    } else {
      detail = `Done — ${totalIndexed} indexed`;
    }

    // Representative result envelope drives the per-job completion CTA
    // ("View redacted copy" / "Download" / "View checklist") for registry
    // job types. The list endpoint mirrors result into progress on terminal
    // jobs, so fall back across both.
    const latestCompleted = completed.reduce((best, j) =>
      (!best || (j.created_at || '') > (best.created_at || '')) ? j : best
    , null);

    return {
      key: `${type}-done`,
      kind: totalFailed > 0 ? 'done-with-failures' : 'done',
      jobType: type,
      result: latestCompleted?.result || latestCompleted?.progress || {},
      label: jobLabel(type),
      detail,
      failedDetails,
      pct: 100,
      elapsed: 0,
    };
  }

  if (failed.length > 0) {
    // Only surface failures when no active job has taken over.  Show the
    // most recent failure's error message.
    const latest = failed.reduce((best, j) =>
      (!best || (j.created_at || '') > (best.created_at || '')) ? j : best
    , null);
    return {
      key: `${type}-failed`,
      kind: 'failed',
      jobType: type,
      label: jobLabel(type),
      detail: `Failed: ${latest?.error || 'unknown error'}`,
      pct: null,
      elapsed: 0,
    };
  }

  return null;
}

export default function BackgroundJobsBanner({ caseId }) {
  // Job state comes from the single case-wide poller in BackgroundJobsProvider
  // (no per-banner setInterval). Completion toasts fire from the provider; this
  // banner is the persistent in-page progress + CTA surface.
  const { jobs } = useBackgroundJobs();
  const [expandedKey, setExpandedKey] = useState(null);
  const navigate = useNavigate();

  if (jobs.length === 0) return null;

  // Group by job_type and emit one row per type. Auto enrichment fan-out
  // (graph/entity extraction the pipeline drives toward "ready") is tagged
  // initiated_by="auto" — it's normal ingestion, not a user action, so it
  // never banners here. The ActivityMonitor folds it into "Processing".
  const grouped = {};
  for (const j of jobs) {
    if (j.params?.initiated_by === 'auto') continue;
    if (!grouped[j.job_type]) grouped[j.job_type] = [];
    grouped[j.job_type].push(j);
  }
  const rows = Object.entries(grouped)
    .map(([type, groupJobs]) => summarizeGroup(type, groupJobs))
    .filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div className="bg-jobs-banner">
      {rows.map(row => {
        const hasFailures = row.failedDetails && row.failedDetails.length > 0;
        const expanded = expandedKey === row.key;
        // Registry-driven completion CTA on a finished job (e.g. "View redacted
        // copy" / "Download" / "View checklist"). Aggregate types without a
        // registry completion render no CTA.
        const cta = (row.kind === 'done' || row.kind === 'done-with-failures')
          && JOB_REGISTRY[row.jobType]?.completion
          ? jobCompletion(row.jobType, row.result, { caseId })
          : null;
        return (
          <div
            key={row.key}
            className={
              `bg-job-item`
              + (row.kind === 'done' ? ' bg-job-done' : '')
              + (row.kind === 'done-with-failures' ? ' bg-job-done-with-failures' : '')
              + (row.kind === 'failed' ? ' bg-job-failed' : '')
            }
          >
            <span className={
              row.kind === 'done' ? 'bg-job-check'
                : row.kind === 'failed' ? 'bg-job-x'
                : row.kind === 'done-with-failures' ? 'bg-job-warn'
                : 'bg-job-pulse'
            } />
            <span className="bg-job-label">{row.label}</span>
            {row.detail && <span className="bg-job-detail">{row.detail}</span>}
            {hasFailures && (
              <button
                type="button"
                className="bg-job-detail-toggle"
                onClick={() => setExpandedKey(expanded ? null : row.key)}
              >
                {expanded ? 'Hide details' : 'Show details'}
              </button>
            )}
            {row.pct !== null && (
              <div className="bg-job-bar">
                <div className="bg-job-bar-fill" style={{ width: `${row.pct}%` }} />
              </div>
            )}
            {row.kind === 'active' && row.elapsed > 0 && (
              <span className="bg-job-elapsed">{formatElapsed(row.elapsed)}</span>
            )}
            {cta?.cta && cta?.href && (
              <button
                type="button"
                className="bg-job-cta"
                onClick={() => followHref(navigate, cta.href)}
              >
                {cta.cta}
              </button>
            )}
            {cta?.secondary?.cta && cta?.secondary?.href && (
              <button
                type="button"
                className="bg-job-cta bg-job-cta-secondary"
                onClick={() => followHref(navigate, cta.secondary.href)}
              >
                {cta.secondary.cta}
              </button>
            )}
            {cta?.secondary?.cta && cta?.secondary?.event && (
              <button
                type="button"
                className="bg-job-cta bg-job-cta-secondary"
                onClick={() => window.dispatchEvent(new CustomEvent(cta.secondary.event))}
              >
                {cta.secondary.cta}
              </button>
            )}
            {hasFailures && expanded && (
              <ul className="bg-job-failure-list">
                {row.failedDetails.map((f, i) => (
                  <li key={`${f.document_id || i}`}>
                    <span className="bg-job-failure-name">
                      {f.filename || f.document_id || 'unknown document'}
                    </span>
                    <span className="bg-job-failure-reason">
                      {f.error_type ? `${f.error_type}: ` : ''}{f.error || 'unknown error'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
